/**
 * Bundle Builder — bundle-builder.js  v2.0
 * Shopify Online Store 2.0 compatible
 *
 * FIXED in v2:
 *  1. Cart drawer now refreshes correctly via Shopify Section Rendering API
 *     BEFORE signalling open — so the drawer always shows fresh items.
 *  2. Smarter theme detection: reads the DOM to identify which theme
 *     pattern is present, then uses only the right mechanism.
 *  3. Sticky footer fully visible — z-index + safe-area padding handled in CSS.
 */

(function () {
  'use strict';

  /* ─── Helpers ─────────────────────────────────────────── */

  function formatMoney(cents, moneyFormat) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat);
    }
    // Simple rupee / generic fallback
    const amount = (cents / 100).toFixed(2);
    if (!moneyFormat) return amount;
    return moneyFormat
      .replace('{{amount}}',             parseFloat((cents / 100).toFixed(2)))
      .replace('{{amount_with_comma_separator}}', parseFloat((cents / 100).toFixed(2)))
      .replace('{{amount_no_decimals}}',  Math.round(cents / 100));
  }

  /* ─── BundleBuilder ───────────────────────────────────── */

  class BundleBuilder {
    constructor(el) {
      this.el        = el;
      this.sectionId = el.dataset.sectionId;
      this.bundleQty = parseInt(el.dataset.bundleQty, 10)    || 3;
      this.bundlePrice = parseInt(el.dataset.bundlePrice, 10) || 89900; // cents

      const cfgEl  = document.getElementById(`bundle-config-${this.sectionId}`);
      this.config  = cfgEl ? JSON.parse(cfgEl.textContent) : {};

      /** Map<variantId, {variantId, productId, title, image, price, compareAt, qty}> */
      this.selection = new Map();
      this._busy     = false; // prevent double-submit

      this._dom();
      this._bind();
      this._render();
    }

    /* ── DOM refs ── */
    _dom() {
      const s = this.sectionId;
      this.$ = {
        fill:    document.getElementById(`bundle-progress-fill-${s}`),
        label:   document.getElementById(`bundle-progress-label-${s}`),
        footer:  document.getElementById(`bundle-footer-${s}`),
        count:   document.getElementById(`bundle-count-${s}`),
        thumbs:  document.getElementById(`bundle-thumbs-${s}`),
        pricing: document.getElementById(`bundle-pricing-${s}`),
        compare: document.getElementById(`bundle-compare-price-${s}`),
        savings: document.getElementById(`bundle-savings-${s}`),
        cta:     document.getElementById(`bundle-cta-${s}`),
        cards:   this.el.querySelectorAll('.bundle-card'),
      };
    }

    /* ── Events ── */
    _bind() {
      this.el.addEventListener('click', e => this._onClick(e));
      this.$.cta?.addEventListener('click', () => this._addToCart());
    }

    _onClick(e) {
      const card = e.target.closest('.bundle-card');
      if (!card || card.dataset.available === 'false') return;

      const vid  = card.dataset.variantId;
      const meta = {
        variantId: vid,
        productId: card.dataset.productId,
        title:     card.dataset.productTitle,
        image:     card.dataset.productImage,
        price:     parseInt(card.dataset.productPrice,  10) || 0,
        compareAt: parseInt(card.dataset.productCompare,10) || 0,
      };

      // Qty ± buttons (only when card is already selected)
      const qtyBtn = e.target.closest('.bundle-card__qty-btn');
      if (qtyBtn && this.selection.has(vid)) {
        e.stopPropagation();
        const item = this.selection.get(vid);
        if (qtyBtn.dataset.action === 'plus') {
          if (this._total() < this.bundleQty) { item.qty++; this.selection.set(vid, item); }
          else this._shake();
        } else {
          if (item.qty > 1) { item.qty--; this.selection.set(vid, item); }
          else              this.selection.delete(vid);
        }
        this._render();
        return;
      }

      // "Add to Box" button
      if (e.target.closest('.bundle-card__add-btn')) {
        e.stopPropagation();
        if (!this.selection.has(vid)) this._pick(meta);
        return;
      }

      // Click anywhere on card = toggle
      if (this.selection.has(vid)) { this.selection.delete(vid); this._render(); }
      else                          this._pick(meta);
    }

    _pick(meta) {
      if (this._total() >= this.bundleQty) { this._shake(); return; }
      this.selection.set(meta.variantId, { ...meta, qty: 1 });
      this._render();
    }

    _total() {
      let n = 0;
      this.selection.forEach(i => { n += i.qty; });
      return n;
    }

    /* ── Render all UI ── */
    _render() {
      const total     = this._total();
      const full      = total >= this.bundleQty;
      const remaining = this.bundleQty - total;
      const pct       = Math.min(100, Math.round((total / this.bundleQty) * 100));

      // Progress bar
      if (this.$.fill)  this.$.fill.style.width = `${pct}%`;
      if (this.$.label) {
        if (full) {
          this.$.label.textContent    = '🎉 Bundle complete! Click Add to Cart.';
          this.$.label.style.color      = '#2d7a47';
          this.$.label.style.fontWeight = '700';
        } else {
          this.$.label.textContent    = `Select ${remaining} more item${remaining !== 1 ? 's' : ''} to complete your bundle`;
          this.$.label.style.color      = '';
          this.$.label.style.fontWeight = '';
        }
      }

      // Count chip
      if (this.$.count) this.$.count.textContent = `${total}/${this.bundleQty}`;

      // Footer slide-up
      if (this.$.footer) this.$.footer.classList.toggle('is-visible', total > 0);

      // Thumbnails
      this._renderThumbs(total);

      // Pricing block
      this._renderPricing(total);

      // CTA
      const cta    = this.$.cta;
      const textEl = cta?.querySelector('.bundle-footer__cta-text');
      if (cta && !this._busy) {
        cta.disabled = !full;
        cta.setAttribute('aria-disabled', String(!full));
        if (textEl && !cta.classList.contains('is-success')) {
          textEl.textContent = cta.dataset.defaultText || textEl.textContent;
        }
      }

      // Cards
      this._renderCards();
    }

    _renderThumbs(total) {
      if (!this.$.thumbs) return;
      const slots = this.$.thumbs.querySelectorAll('.bundle-thumb');
      const flat  = [];
      this.selection.forEach(item => {
        for (let i = 0; i < item.qty; i++) flat.push(item);
      });
      slots.forEach((slot, i) => {
        const item = flat[i];
        if (item) {
          slot.classList.add('is-filled');
          slot.classList.remove('bundle-thumb--empty');
          let img = slot.querySelector('img');
          if (!img) { img = document.createElement('img'); img.width = 48; img.height = 48; slot.innerHTML = ''; slot.appendChild(img); }
          img.src = item.image || '';
          img.alt = item.title || '';
        } else {
          slot.classList.remove('is-filled');
          slot.classList.add('bundle-thumb--empty');
          slot.innerHTML = '<span class="bundle-thumb__plus">+</span>';
        }
      });
    }

    _renderPricing(total) {
      if (!this.$.pricing) return;
      this.$.pricing.style.display = total === 0 ? 'none' : '';
      if (total === 0) return;

      let compareTotal = 0;
      this.selection.forEach(item => {
        compareTotal += (item.compareAt > item.price ? item.compareAt : item.price) * item.qty;
      });

      const savings    = compareTotal - this.bundlePrice;
      const savingsPct = compareTotal > 0 ? Math.round((savings / compareTotal) * 100) : 0;

      if (this.$.compare) this.$.compare.textContent = compareTotal > 0 ? formatMoney(compareTotal, this.config.moneyFormat) : '';
      if (this.$.savings) {
        this.$.savings.textContent   = savings > 0 ? `Save ${savingsPct}%` : '';
        this.$.savings.style.display = savings > 0 ? '' : 'none';
      }
    }

    _renderCards() {
      this.$.cards.forEach(card => {
        const vid      = card.dataset.variantId;
        const selected = this.selection.has(vid);
        const item     = selected ? this.selection.get(vid) : null;

        card.classList.toggle('is-selected', selected);

        const qtyEl  = card.querySelector('.bundle-card__qty');
        const qtyWrap= card.querySelector('.bundle-card__qty-wrap');
        const addBtn = card.querySelector('.bundle-card__add-btn');

        if (qtyEl  && item) qtyEl.textContent = item.qty;
        if (qtyWrap)        qtyWrap.setAttribute('aria-hidden', String(!selected));
        if (addBtn) {
          if (!addBtn.dataset.defaultText) addBtn.dataset.defaultText = addBtn.textContent.trim();
          addBtn.textContent = selected ? 'Added ✓' : addBtn.dataset.defaultText;
        }
      });
    }

    /* ── Shake footer CTA ── */
    _shake() {
      const cta = this.$.cta;
      if (!cta) return;
      cta.classList.remove('is-shaking');
      void cta.offsetWidth;
      cta.classList.add('is-shaking');
      cta.addEventListener('animationend', () => cta.classList.remove('is-shaking'), { once: true });
    }

    /* ════════════════════════════════════════════════════════
       ADD TO CART
       Strategy:
         1. POST all items to /cart/add.js
         2. Refresh the cart drawer HTML via Section Rendering API
         3. Open / animate the drawer
         4. Update header cart count
    ════════════════════════════════════════════════════════ */
    async _addToCart() {
      if (this._busy || this._total() < this.bundleQty) { this._shake(); return; }
      this._busy = true;

      const cta    = this.$.cta;
      const textEl = cta?.querySelector('.bundle-footer__cta-text');

      // Loading state
      if (cta) { cta.disabled = true; cta.classList.add('is-loading'); }
      if (textEl) textEl.textContent = cta?.dataset.loadingText || 'Adding…';

      try {
        // ── Step 1: Add items to cart ──────────────────────
        const items = [];
        this.selection.forEach(item => {
          items.push({
            id:         parseInt(item.variantId, 10),
            quantity:   item.qty,
            properties: {
              _bundle_id:  `bundle-${this.sectionId}`,
              _bundle_tag: this.config.bundleTag || 'bundle-item',
            },
          });
        });

        const addRes = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body:    JSON.stringify({ items }),
        });
        if (!addRes.ok) {
          const err = await addRes.json().catch(() => ({}));
          throw new Error(err.description || `cart/add.js failed: ${addRes.status}`);
        }

        // ── Step 2: Apply discount code (optional) ─────────
        if (this.config.discountCode) {
          await fetch(`/discount/${encodeURIComponent(this.config.discountCode)}`).catch(() => {});
        }

        // ── Step 3: Refresh cart drawer HTML ───────────────
        await this._refreshCartDrawer();

        // ── Step 4: Open drawer ────────────────────────────
        this._openDrawer();

        // ── Step 5: Update header count ────────────────────
        this._updateCount();

        // ── Success state ──────────────────────────────────
        if (cta)    { cta.classList.remove('is-loading'); cta.classList.add('is-success'); }
        if (textEl) textEl.textContent = cta?.dataset.successText || 'Added! View Cart →';

        if (this.config.cartRedirect) window.location.href = '/cart';

      } catch (err) {
        console.error('[BundleBuilder]', err);
        if (cta)    { cta.classList.remove('is-loading'); cta.disabled = false; }
        if (textEl) textEl.textContent = 'Error — Try Again';
      } finally {
        this._busy = false;
      }
    }

    /* ════════════════════════════════════════════════════════
       _refreshCartDrawer
       Minimog theme: calls MinimogTheme.Cart API directly.
         Cart.refreshCart()   → syncs Cart.cart with /cart.json
         Cart.renderNewCart() → re-fetches cart-drawer section
                                and patches .scd__body / .scd__summary
       Other themes: falls back to Shopify Section Rendering API.
    ════════════════════════════════════════════════════════ */
    async _refreshCartDrawer() {

      // ── Minimog (primary path) ────────────────────────────
      const MinimogCart = window.MinimogTheme?.Cart;
      if (MinimogCart) {
        try {
          await MinimogCart.refreshCart();
          await MinimogCart.renderNewCart();
          return; // done — Minimog handled it
        } catch (err) {
          console.warn('[BundleBuilder] MinimogCart refresh failed, using fallback:', err);
        }
      }

      // ── Generic: Shopify Section Rendering API ────────────
      const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
      const SECTIONS = [
        { id: 'cart-drawer',       sel: '#shopify-section-cart-drawer, cart-drawer, #CartDrawer, .cart-drawer[data-section-id], [data-section-id="cart-drawer"]' },
        { id: 'mini-cart',         sel: '#shopify-section-mini-cart, #MiniCart, .mini-cart, [data-section-id="mini-cart"]' },
        { id: 'cart-notification', sel: '#shopify-section-cart-notification, cart-notification, .cart-notification' },
        { id: 'ajax-cart',         sel: '#shopify-section-ajax-cart, #AjaxCart, .ajaxcart__inner' },
        { id: 'cart-items',        sel: '#shopify-section-cart-items, [data-section-id="cart-items"]' },
      ];

      for (const { id, sel } of SECTIONS) {
        const containers = [...document.querySelectorAll(sel)];
        if (!containers.length) continue;

        try {
          const res = await fetch(`${root}/?section_id=${id}`, {
            headers: { Accept: 'text/html' },
            cache:   'no-store',
          });
          if (!res.ok) continue;

          const html         = await res.text();
          const freshDoc     = new DOMParser().parseFromString(html, 'text/html');
          const freshWrapper = freshDoc.getElementById(`shopify-section-${id}`);
          const freshContent = freshWrapper || freshDoc.body;

          containers.forEach(live => {
            const isWrapper = live.id === `shopify-section-${id}`;
            const keepClasses = [...live.classList].filter(c =>
              ['is-open','is-active','is-visible','open','active',
               'drawer--is-open','js-drawer-open','menu-opening'].includes(c)
            );
            const wasOpen    = live.hasAttribute('open');
            const ariaHidden = live.getAttribute('aria-hidden');

            live.innerHTML = (isWrapper && freshWrapper)
              ? freshWrapper.innerHTML
              : freshContent.innerHTML;

            keepClasses.forEach(c => live.classList.add(c));
            if (wasOpen)             live.setAttribute('open', '');
            if (ariaHidden !== null) live.setAttribute('aria-hidden', ariaHidden);

            live.querySelectorAll('script').forEach(old => {
              const s = document.createElement('script');
              [...old.attributes].forEach(a => s.setAttribute(a.name, a.value));
              s.textContent = old.textContent;
              old.replaceWith(s);
            });

            if (typeof live.renderContents === 'function') {
              try { live.renderContents({ sections: { [id]: html } }); } catch (_) {}
            }
          });

          this._patchInnerEls(freshDoc);
          return;
        } catch (_) { continue; }
      }

      // ── Last resort: patch subtotal only ─────────────────
      try {
        const cart = await fetch('/cart.js', {
          headers: { Accept: 'application/json' }, cache: 'no-store',
        }).then(r => r.json());
        document.querySelectorAll(
          '[data-cart-subtotal],[data-subtotal],.cart-subtotal,' +
          '.totals__subtotal,#CartDrawer-Subtotal,.mini-cart__subtotal-amount'
        ).forEach(el => { el.textContent = formatMoney(cart.total_price, this.config.moneyFormat); });
      } catch (_) {}
    }

    /* Surgically patch inner cart sub-elements */
    _patchInnerEls(freshDoc) {
      const PAIRS = [
        '#CartDrawer-CartItems',      '#CartDrawer-CartItems',
        '#CartDrawer-Footer',         '#CartDrawer-Footer',
        '.cart-drawer__inner',        '.cart-drawer__inner',
        '.cart-drawer__footer',       '.cart-drawer__footer',
        '[data-cart-drawer-items]',   '[data-cart-drawer-items]',
        '.mini-cart__items',          '.mini-cart__items',
        '.mini-cart__footer',         '.mini-cart__footer',
        '.ajaxcart__body',            '.ajaxcart__body',
        '.ajaxcart__footer',          '.ajaxcart__footer',
        '#cart-notification-product', '#cart-notification-product',
      ];
      for (let i = 0; i < PAIRS.length; i += 2) {
        const live  = document.querySelector(PAIRS[i]);
        const fresh = freshDoc.querySelector(PAIRS[i + 1]);
        if (live && fresh) live.innerHTML = fresh.innerHTML;
      }
    }

    /* ════════════════════════════════════════════════════════
       _openDrawer
       Minimog: calls MinimogTheme.Cart.openCartDrawer() and
       emits MinimogEvents so the theme's own Cart class handles
       the CSS transition.
       Other themes: fires all generic open signals.
    ════════════════════════════════════════════════════════ */
    _openDrawer() {

      // ── Minimog (primary) ─────────────────────────────────
      // openCartDrawer() in Minimog:
      //   - removes 'hidden' from .scd__wrapper
      //   - removes translate-x-full from .scd__content
      //   - emits ON_OPEN_DRAWER_CART
      //   - adds scroll lock on mobile
      try {
        const MinimogCart = window.MinimogTheme?.Cart;
        if (MinimogCart) {
          if (typeof MinimogCart.openCartDrawer === 'function') {
            MinimogCart.openCartDrawer();
          }
          // Also emit via Minimog's event bus so any subscribers react
          if (typeof window.MinimogEvents?.emit === 'function') {
            window.MinimogEvents.emit('ON_OPEN_DRAWER_CART', MinimogCart.cart || {});
          }
          // Also update Minimog cart counts (.sf-cart-count elements)
          if (typeof window.MinimogTheme?.Cart?.updateCartCount === 'function') {
            window.MinimogTheme.Cart.updateCartCount(MinimogCart.cart || {});
          }
          return; // Minimog handled it
        }
      } catch (err) {
        console.warn('[BundleBuilder] Minimog openCartDrawer failed:', err);
      }

      // ── Dawn PubSub ───────────────────────────────────────
      try {
        if (typeof window.publish === 'function') {
          window.publish('cart:open');
          window.publish('cart:refresh');
        }
      } catch (_) {}

      // ── Dawn <cart-drawer> custom element ─────────────────
      try {
        const dawnEl = document.querySelector('cart-drawer, #CartDrawer, #shopify-section-cart-drawer cart-drawer');
        if (dawnEl) {
          if (typeof dawnEl.open === 'function') dawnEl.open();
          dawnEl.setAttribute('open', '');
          dawnEl.removeAttribute('hidden');
          dawnEl.classList.add('is-open', 'active');
          dawnEl.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
        }
      } catch (_) {}

      // ── Dawn cart-notification ────────────────────────────
      try { const n = document.querySelector('cart-notification'); if (n?.open) n.open(); } catch (_) {}

      // ── Global CartDrawer object ──────────────────────────
      try { if (window.CartDrawer?.open)    window.CartDrawer.open();    } catch (_) {}
      try { if (window.CartDrawer?.refresh) window.CartDrawer.refresh(); } catch (_) {}

      // ── Archetype themes ──────────────────────────────────
      try { if (window.theme?.cartDrawer?.open)    window.theme.cartDrawer.open();    } catch (_) {}
      try { if (window.theme?.cartDrawer?.refresh) window.theme.cartDrawer.refresh(); } catch (_) {}

      // ── jQuery events ─────────────────────────────────────
      try {
        const jq = window.$ || window.jQuery;
        if (jq) {
          jq('body').trigger('added.ajaxProduct');
          jq('body').trigger('cart.requestComplete');
          jq('body').trigger('atc:success');
          jq('body').trigger('update.ajaxCart');
          jq(document).trigger('cart:refresh');
          jq(document).trigger('cart:open');
        }
      } catch (_) {}

      // ── Out of the Sandbox ────────────────────────────────
      try { if (window.CartJS?.refresh) window.CartJS.refresh(); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('ajaxCart:afterAdd',        { bubbles: true })); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent('ajaxCart:requestComplete', { bubbles: true })); } catch (_) {}

      // ── Broadcast ─────────────────────────────────────────
      try {
        const bc = document.querySelector('[data-cart-drawer], .cart-drawer');
        if (bc) { bc.classList.add('is-open', 'is-visible'); bc.setAttribute('aria-hidden', 'false'); }
        document.dispatchEvent(new CustomEvent('broadcast:cartOpen', { bubbles: true }));
      } catch (_) {}

      // ── Pixel Union ───────────────────────────────────────
      try { if (window.ajaxCart?.load) window.ajaxCart.load(); } catch (_) {}

      // ── Generic broadcast ─────────────────────────────────
      [
        'cart:open','cart:refresh','cart:updated','cart:change',
        'CartDrawer:open','cartdrawer:open','open-cart-drawer',
        'cart-open','cart-refresh','cartOpen','cartRefresh',
        'updateCart','refreshCart','header:cart:open','theme:cart:open',
        'mini-cart:open','minicart:open','mini_cart_open',
        'nv:cart:open','nb:cart:open',
        'sticky-cart:open','booster:cart:open','debutify:cart:open',
      ].forEach(name => {
        try {
          const ev = new CustomEvent(name, { bubbles: true, detail: { source: 'bundle-builder' } });
          document.dispatchEvent(ev);
          window.dispatchEvent(ev);
        } catch (_) {}
      });

      // ── DOM fallback ──────────────────────────────────────
      setTimeout(() => {
        const isOpen = document.querySelector(
          'cart-drawer[open], cart-drawer.is-open, #CartDrawer.is-open, ' +
          '.cart-drawer.is-open, [data-cart-drawer].is-open, ' +
          '.scd__wrapper:not(.hidden), .js-cart-drawer.is-active'
        );
        if (!isOpen) {
          const btn =
            document.querySelector('[data-cart-toggle]')    ||
            document.querySelector('[data-open-cart]')      ||
            document.querySelector('.sf-cart-icon')         ||
            document.querySelector('.js-cart-toggle')       ||
            document.querySelector('.cart-toggle')          ||
            document.querySelector('#CartIconBubble')       ||
            document.querySelector('.icon-cart')            ||
            document.querySelector('[aria-label*="cart" i]');
          if (btn?.click) btn.click();
        }
      }, 400);
    }

    /* ── Update header cart count badges ── */
    async _updateCount() {
      try {
        const cart  = await fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(r => r.json());
        const count = cart.item_count;
        document.querySelectorAll(
          // Minimog uses .sf-cart-count
          '.sf-cart-count,' +
          '[data-cart-count],[data-cart-item-count],[data-item-count],' +
          '.cart-count,.cart__count,.cart-count-bubble,.header__cart-count,' +
          '.site-header__cart-count,.nav-cart-count,#CartCount,' +
          '#cart-icon-bubble,.cart-icon-bubble,[data-header-cart-count],' +
          '.icon-cart__count,.cart__badge,.cart-link__count,.header-cart-btn__count'
        ).forEach(el => {
          el.textContent   = count;
          el.style.display = count > 0 ? '' : 'none';
        });
        if (window.Shopify) window.Shopify.__cartCount = count;
        document.dispatchEvent(new CustomEvent('count:update', { bubbles: true, detail: { count } }));
        // Minimog: also call updateCartCount if Cart exists
        if (typeof window.MinimogTheme?.Cart?.updateCartCount === 'function') {
          window.MinimogTheme.Cart.updateCartCount(cart);
        }
      } catch (_) {}
    }
  }

  /* ─── Boot ──────────────────────────────────────────── */

  function init() {
    document.querySelectorAll('.bundle-builder[data-section-id]').forEach(el => {
      if (!el.__bb) el.__bb = new BundleBuilder(el);
    });
  }

  document.addEventListener('shopify:section:load', e => {
    const el = e.target.querySelector('.bundle-builder[data-section-id]');
    if (el) el.__bb = new BundleBuilder(el);
  });
  document.addEventListener('shopify:section:reorder', init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

})();
